import { hydrateFilterShortcode } from './wgf-filters.js';
import { buildCatalogUrl, escapeHtml, navigateTo, normalizeCalculatorConfig, parseJsonObject, t, ensureString, wgfWarn } from './wgf-utils.js';

function buildCalculatorSelect(label, key, definition = {}, filterKey = '') {
  const terms = Array.isArray(definition.terms) ? definition.terms : [];
  const placeholder = typeof definition.placeholder === 'string' ? definition.placeholder : t('choose', 'Elegir');
  const emptyMessage = typeof definition.emptyMessage === 'string' ? definition.emptyMessage : t('noOptions', 'Sin opciones disponibles');
  const disabled = Boolean(definition.disabled) || terms.length === 0;
  const options = terms.map((term) => `<option value="${escapeHtml(term?.slug || '')}">${escapeHtml(term?.name || '')}</option>`).join('');
  const nameAttribute = filterKey ? ` name="${escapeHtml(filterKey)}"` : '';
  return '<div class="wgf-calc-field">' +
    `<span class="wgf-calc-field-label">${escapeHtml(label)}</span>` +
    `<select class="wgf-calc-select" data-wgf-answer="${escapeHtml(key)}"${nameAttribute}${disabled ? ' disabled aria-disabled="true"' : ''}>` +
    `<option value="">${escapeHtml(placeholder)}</option>${options}</select>` +
    `${disabled ? `<p class="wgf-calc-empty" data-wgf-empty-state="true">${escapeHtml(emptyMessage)}</p>` : ''}` +
    '</div>';
}

function hydrateCalculator(root) {
  const config = normalizeCalculatorConfig(parseJsonObject(root.dataset.wgfCalculatorConfig || '{}'));
  const context = config.context;
  const fromCalculatorResult = (() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      return params.get('wgf_calc_result') === '1';
    } catch (error) {
      return false;
    }
  })();
  const resultModeQuestionText = t('recommendation', 'Te recomendamos las siguientes opciones para cubrir esta necesidad de refrigeración:');

  const stepper = root.querySelector('[data-wgf-stepper="root"]');
  if (!stepper) return;

  const mapping = Object.keys(config.calculatorMapping || {}).length ? config.calculatorMapping : {};
  const actionUrl = config.catalogUrl;
  stepper.innerHTML = '<div class="wgf-calc-wizard" data-wgf-wizard="root"></div>';
  const wizard = root.querySelector('[data-wgf-wizard="root"]');
  if (!wizard) return;

  const options = config.calculatorOptions;
  const configuredSteps = Array.isArray(config.calculatorSteps) ? config.calculatorSteps : [];
  const defaultSteps = [
    { key: 'step_1', title: '', button: t('next', 'Siguiente'), taxonomy: '' },
    { key: 'step_2', title: '', button: t('next', 'Siguiente'), taxonomy: '' },
    { key: 'step_3', title: '', button: t('submit', 'Enviar'), taxonomy: '' },
  ];

  const baseSteps = configuredSteps.length ? configuredSteps : defaultSteps;
  const rawSteps = baseSteps.map((step, index) => {
    const key = typeof step?.key === 'string' && step.key.trim() ? step.key.trim() : `step_${index + 1}`;
    const optionDefinition = options[key] || {};
    const taxonomy = (typeof step?.taxonomy === 'string' && step.taxonomy.trim()) ? step.taxonomy.trim() : (typeof optionDefinition?.taxonomy === 'string' ? optionDefinition.taxonomy : '');
    const fallbackFilter = taxonomy ? `filter_${taxonomy}` : '';
    const filterKey = ensureString(mapping[key], fallbackFilter);
    const hasExplicitTitle = typeof step?.title === 'string';
    return {
      key,
      title: hasExplicitTitle ? step.title : `${t('questionFallback', 'Pregunta')} ${index + 1}`,
      button: typeof step?.button === 'string' && step.button.trim() ? step.button : (index === baseSteps.length - 1 ? t('submit', 'Enviar') : t('next', 'Siguiente')),
      definition: optionDefinition,
      filterKey,
    };
  });

  const steps = rawSteps.filter((step) => step.filterKey);
  if (rawSteps.length && steps.length < rawSteps.length) {
    wgfWarn('Some calculator steps were ignored because they have no valid filter key.', { total: rawSteps.length, active: steps.length });
  }
  if (!steps.length) {
    wgfWarn('No valid calculator steps found after normalization.');
    return;
  }

  const answers = {};
  steps.forEach((step) => { answers[step.key] = ''; });
  let currentStep = 0;

  const renderStep = () => {
    const step = steps[currentStep];
    const label = context === 'inline' ? t('pickOption', 'Elegí una opción') : (step.definition?.label || t('selectOption', 'Seleccioná una opción'));
    const control = buildCalculatorSelect(label, step.key, step.definition || {}, step.filterKey);
    const backButton = currentStep > 0
      ? `<button class="wgf-calc-next wgf-calc-back" type="button" data-wgf-back="true" aria-label="${escapeHtml(t('back', 'Volver'))}"><span class="wgf-calc-back-icon" aria-hidden="true">←</span><span class="wgf-calc-back-text">${escapeHtml(t('back', 'Volver'))}</span></button>`
      : '';
    const displayQuestion = fromCalculatorResult ? resultModeQuestionText : step.title;
    const headerMarkup = context === 'inline' ? '' : `<header class="wgf-calc-header"><h2 class="wgf-calc-title">${escapeHtml(t('calcTitle', 'CALCULÁ TU EQUIPO IDEAL'))}</h2></header>${displayQuestion ? `<p class="wgf-calc-question">${escapeHtml(displayQuestion)}</p>` : ''}`;
    const actionsClass = context === 'inline' ? 'wgf-calc-actions wgf-calc-actions--inline' : 'wgf-calc-actions';

    wizard.innerHTML = `<form class="wgf-calc-form" data-wgf-calculator-form="root" method="get" action="${escapeHtml(actionUrl)}" target="_top">${headerMarkup}<div class="wgf-calc-controls" data-wgf-calculator-selects="root">${control}</div><div class="${actionsClass}">${backButton}<button class="wgf-calc-next" type="submit" data-wgf-submit="true">${escapeHtml(step.button)}</button></div></form>`;

    if (context === 'inline') {
      const scope = root.closest('.elementor') || root.parentElement || document;
      const inlineTitle = scope.querySelector('.titulo-inline');
      if (inlineTitle) {
        const headingTextNode = inlineTitle.querySelector('.elementor-heading-title');
        if (headingTextNode) headingTextNode.textContent = displayQuestion;
        else inlineTitle.textContent = displayQuestion;
      }
    }

    const select = wizard.querySelector(`[data-wgf-answer="${step.key}"]`);
    if (select) select.value = answers[step.key] || '';

    const submitButton = wizard.querySelector('[data-wgf-submit="true"]');
    const syncButtonState = () => { if (!submitButton || !select) return; submitButton.disabled = !select.value; };
    syncButtonState();
    if (select) select.addEventListener('change', () => { answers[step.key] = select.value || ''; syncButtonState(); });

    const form = wizard.querySelector('[data-wgf-calculator-form="root"]');
    if (!form) return;
    const back = wizard.querySelector('[data-wgf-back="true"]');
    if (back) back.addEventListener('click', () => { if (currentStep === 0) return; currentStep -= 1; renderStep(); });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!answers[step.key]) return;
      if (currentStep < steps.length - 1) { currentStep += 1; renderStep(); return; }

      const canonical = {};
      steps.forEach((candidate) => {
        if (!candidate.filterKey) return;
        const value = answers[candidate.key] || '';
        if (!value) return;
        canonical[candidate.filterKey] = value;
      });

      const destination = new URL(buildCatalogUrl(canonical, config), window.location.origin);
      destination.searchParams.set('wgf_calc_result', '1');
      navigateTo(destination.toString());
    });
  };

  renderStep();
}

function hydrateProducts(root) {
  const equalizeAttr = (root.dataset.wgfEqualize || '1').toLowerCase();
  const equalizeEnabled = !['0', 'false', 'off', 'no'].includes(equalizeAttr);
  if (!equalizeEnabled) return;

  const equalizeMobileAttr = (root.dataset.wgfEqualizeMobile || '0').toLowerCase();
  const equalizeMobileEnabled = !['0', 'false', 'off', 'no'].includes(equalizeMobileAttr);
  if (!equalizeMobileEnabled && typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches) return;

  const items = Array.from(root.querySelectorAll('.wgf-products__item'));
  if (!items.length) return;
  const configuredMinHeight = Number(root.dataset.wgfMinHeight || 0);
  const equalize = () => {
    items.forEach((item) => { item.style.height = ''; item.style.minHeight = ''; });
    let maxHeight = 0;
    items.forEach((item) => { const current = item.offsetHeight || 0; if (current > maxHeight) maxHeight = current; });
    const targetHeight = Math.max(maxHeight, Number.isFinite(configuredMinHeight) ? configuredMinHeight : 0);
    if (targetHeight < 1) return;
    items.forEach((item) => { item.style.height = `${targetHeight}px`; item.style.minHeight = `${targetHeight}px`; });
  };
  requestAnimationFrame(equalize);
}

function bootGuidedFilters(doc = document) {
  doc.querySelectorAll('[data-wgf-component="filters"]').forEach((root) => hydrateFilterShortcode(root));
  doc.querySelectorAll('[data-wgf-component="calculator"]').forEach((root) => hydrateCalculator(root));
  doc.querySelectorAll('[data-wgf-component="products"]').forEach((root) => hydrateProducts(root));
}

function setupPopupCalculatorToggle(doc = document) {
  if (doc.body?.dataset?.wgfPopupBound === 'true') return;
  if (doc.body) doc.body.dataset.wgfPopupBound = 'true';

  const setInitialPopupState = () => {
    const introContainer = doc.getElementById('wgf-popup-intro');
    const wizardContainer = doc.getElementById('wgf-popup-wizard');
    if (introContainer) {
      introContainer.classList.remove('is-fading-out');
      introContainer.classList.remove('is-hidden');
      introContainer.classList.add('is-visible');
    }
    if (wizardContainer) {
      wizardContainer.classList.add('is-hidden');
      wizardContainer.classList.remove('is-visible');
    }
  };

  setInitialPopupState();
  doc.addEventListener('click', (event) => {
    const startButton = event.target.closest('#wgf-popup-start');
    if (!startButton) return;
    event.preventDefault();
    const introContainer = doc.getElementById('wgf-popup-intro');
    const wizardContainer = doc.getElementById('wgf-popup-wizard');
    if (!introContainer || !wizardContainer) return;
    introContainer.classList.add('is-fading-out');
    setTimeout(() => {
      introContainer.classList.add('is-hidden');
      introContainer.classList.remove('is-visible');
      introContainer.classList.remove('is-fading-out');
      wizardContainer.classList.remove('is-hidden');
      wizardContainer.classList.add('is-visible');
    }, 220);
  });

  if (typeof jQuery !== 'undefined') {
    jQuery(document).on('elementor/popup/show', () => { setInitialPopupState(); });
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupPopupCalculatorToggle(document);
      bootGuidedFilters(document);
    });
  } else {
    setupPopupCalculatorToggle(document);
    bootGuidedFilters(document);
  }

  if (typeof jQuery !== 'undefined') {
    jQuery(document).on('elementor/popup/show', () => { bootGuidedFilters(document); });
  }
}
